import React, { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import useHeroParallax from "@/hooks/useHeroParallax";

const HERO_IMG = "/hero/kit-corporativo.webp";

const EASE = [0.22, 1, 0.36, 1];

// Static framer-motion animation configs — declared at module scope so React
// receives the same object reference across renders and skips unnecessary work.
const EYEBROW_INITIAL = { opacity: 0, y: 12 };
const EYEBROW_ANIMATE = { opacity: 1, y: 0 };
const EYEBROW_TRANSITION = { duration: 0.7, ease: EASE, delay: 0.05 };

const TITLE_INITIAL = { opacity: 0, y: 26 };
const TITLE_ANIMATE = { opacity: 1, y: 0 };
const TITLE_TRANSITION = { duration: 0.9, ease: EASE, delay: 0.15 };

const SUB_INITIAL = { opacity: 0, y: 14 };
const SUB_ANIMATE = { opacity: 1, y: 0 };
const SUB_TRANSITION = { duration: 0.8, ease: EASE, delay: 0.4 };

const CTA_TRANSITION = { duration: 0.7, ease: EASE, delay: 0.55 };

const TRUST_INITIAL = { opacity: 0 };
const TRUST_ANIMATE = { opacity: 1 };
const TRUST_TRANSITION = { duration: 0.9, delay: 0.85 };

export const Hero = ({ onQuoteClick }) => {
  const prefersReducedMotion = useReducedMotion();
  const stageRef = useRef(null);
  const imageWrapRef = useRef(null);
  const overlayRef = useRef(null);

  useHeroParallax({
    stageRef,
    imageRef: imageWrapRef,
    overlayRef,
    enabled: !prefersReducedMotion,
  });

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      ref={stageRef}
      id="home"
      data-testid="hero-section"
      className="mn-hero-premium relative isolate overflow-hidden text-white"
      aria-label="Kit corporativo M NAIEF"
    >
      {/* Background photograph */}
      <div className="absolute inset-0 -z-10">
        <div ref={imageWrapRef} className="mn-hero-parallax absolute inset-0 will-change-transform">
          <img
            src={HERO_IMG}
            alt="Kit corporativo premium M NAIEF — sacola de algodão, camiseta, squeeze, caderno e chapéu Panamá em cenário cinematográfico"
            className="w-full h-full object-cover"
            fetchpriority="high"
            data-testid="hero-image"
          />
        </div>
        {/* Cinematic gradient (deep left → warm right glow) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.78) 32%, rgba(10,10,10,0.32) 62%, rgba(10,10,10,0.10) 100%)",
          }}
        />
        {/* Vignette + top-bottom fade */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 100%), linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 24%, transparent 72%, rgba(0,0,0,0.72) 100%)",
          }}
        />
        {/* Warm gold overlay drift (parallax) */}
        <div
          ref={overlayRef}
          className="absolute -right-24 top-1/4 w-[42vw] h-[42vw] rounded-full pointer-events-none will-change-transform"
          style={{
            background:
              "radial-gradient(circle at center, rgba(200,169,106,0.22) 0%, rgba(200,169,106,0.10) 32%, transparent 66%)",
            filter: "blur(30px)",
          }}
        />
        {/* Fine grain */}
        <div className="mn-hero-grain absolute inset-0 pointer-events-none opacity-[0.18] mix-blend-overlay" />
      </div>

      {/* Content — o cabeçalho real (Header.jsx) fica fixo e transparente por
          cima desta seção; o padding-top abaixo só garante que o título não
          fique embaixo dele. */}
      <div className="relative z-10 mn-hero-content mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 pt-32 lg:pt-40 pb-28 sm:pb-32 min-h-[86vh] flex flex-col justify-center">
        <motion.span
          initial={prefersReducedMotion ? false : EYEBROW_INITIAL}
          animate={EYEBROW_ANIMATE}
          transition={EYEBROW_TRANSITION}
          className="text-[0.68rem] sm:text-[0.72rem] uppercase tracking-[0.42em] font-medium text-[var(--mn-gold-soft)]"
          data-testid="hero-eyebrow"
        >
          Vol. 01 — Kit Corporativo Premium
        </motion.span>

        <motion.h1
          initial={prefersReducedMotion ? false : TITLE_INITIAL}
          animate={TITLE_ANIMATE}
          transition={TITLE_TRANSITION}
          className="mn-hero-title mt-8 max-w-[16ch] leading-[0.94]"
          data-testid="hero-title"
        >
          <span className="block font-playfair font-medium text-white">Brindes</span>
          <span className="block font-playfair font-medium text-white">Personalizados</span>
          <span
            className="block font-playfair italic font-medium mt-2"
            style={{ color: "var(--mn-gold-soft)" }}
          >
            para Empresas
          </span>
        </motion.h1>

        <motion.p
          initial={prefersReducedMotion ? false : SUB_INITIAL}
          animate={SUB_ANIMATE}
          transition={SUB_TRANSITION}
          className="mn-hero-sub mt-9 max-w-[46ch] font-inter font-light text-white/72 text-[0.98rem] sm:text-[1.05rem] leading-[1.7]"
          data-testid="hero-subtitle"
        >
          Soluções em brindes personalizados que fortalecem marcas, criam
          experiências e valorizam relacionamentos corporativos.
        </motion.p>

        <motion.div
          initial={prefersReducedMotion ? false : SUB_INITIAL}
          animate={SUB_ANIMATE}
          transition={CTA_TRANSITION}
          className="mt-11 flex flex-col sm:flex-row gap-3.5 sm:gap-4"
          data-testid="hero-ctas"
        >
          <button
            type="button"
            onClick={onQuoteClick}
            className="mn-hero-cta-primary group"
            data-testid="hero-cta-quote"
          >
            <span>Solicitar Orçamento</span>
            <span className="mn-hero-arrow" aria-hidden>
              →
            </span>
          </button>
          <a
            href="#produtos"
            onClick={(e) => scrollTo(e, "#produtos")}
            className="mn-hero-cta-ghost"
            data-testid="hero-cta-collections"
          >
            <span>Ver Coleções</span>
          </a>
        </motion.div>

        {/* Trust row */}
        <motion.div
          initial={prefersReducedMotion ? false : TRUST_INITIAL}
          animate={TRUST_ANIMATE}
          transition={TRUST_TRANSITION}
          className="mt-14 sm:mt-16 flex flex-wrap items-center gap-x-9 gap-y-4"
          data-testid="hero-trust"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[var(--mn-gold-soft)]/70" />
            <span className="text-[0.68rem] uppercase tracking-[0.32em] text-white/55">
              Grupo ROSE&nbsp;S · +20 anos
            </span>
          </div>
          <span className="text-[0.68rem] uppercase tracking-[0.32em] text-white/45">
            +500 projetos entregues
          </span>
          <span className="text-[0.68rem] uppercase tracking-[0.32em] text-white/45">
            98% satisfação
          </span>
        </motion.div>
      </div>

      {/* Right vertical caption */}
      <div
        className="hidden lg:block absolute right-6 xl:right-10 top-1/2 -translate-y-1/2 rotate-90 origin-right pointer-events-none select-none z-10"
        aria-hidden
      >
        <span className="text-[0.62rem] tracking-[0.44em] uppercase text-white/35">
          Vol. 01 — M NAIEF · Guarulhos&nbsp;SP
        </span>
      </div>

      {/* Scroll indicator */}
      <a
        href="#sobre"
        onClick={(e) => scrollTo(e, "#sobre")}
        className="hidden md:flex absolute bottom-8 left-6 sm:left-10 lg:left-16 items-center gap-3 text-white/55 hover:text-[var(--mn-gold-soft)] transition-colors z-10"
        data-testid="hero-scroll"
        aria-label="Rolar para baixo"
      >
        <span className="w-10 h-px bg-current" />
        <span className="text-[0.62rem] uppercase tracking-[0.32em]">Explorar</span>
      </a>
    </section>
  );
};

export default Hero;
